<template>
	<div
		class="chart-state"
		:style="containerStyle"
		:role="isError ? 'alert' : 'status'"
		:aria-live="isError ? 'assertive' : 'polite'"
		data-testid="chart-state"
	>
		<!-- One shape block for two jobs, drawn from one set of geometry so a
		chart cannot pulse as one shape and then settle as another.

		While loading it is the skeleton and the only content, so it survives
		every height. Behind the empty and error copy it is the ghost: purely
		decorative, so an empty chart still reads as a chart rather than a blank
		panel, dropped at cramped heights, and never overlapping the centred
		message whatever the shape. Either way it occupies the footprint a real
		chart would, so the card does not look like it has shrunk. See
		ADR-0012. -->
		<div
			v-if="showShape"
			class="chart-state__shape"
			:class="isLoading ? 'chart-state__skeleton' : 'chart-state__ghost'"
			aria-hidden="true"
			:data-testid="isLoading ? 'chart-state-skeleton' : 'chart-state-ghost'"
		>
			<!-- Vertical bars on a baseline. -->
			<div
				v-if="ghostShape === 'bar'"
				class="chart-state__ghost-plot"
			>
				<div
					v-for="(barSize, index) in ghostBarSizes"
					:key="index"
					class="chart-state__ghost-bar"
					:style="{ height: `${barSize}%` }"
				/>
			</div>
			<!-- The same sizes as widths, growing from a left axis. -->
			<div
				v-else-if="ghostShape === 'bar-horizontal'"
				class="chart-state__ghost-plot chart-state__ghost-plot--horizontal"
			>
				<div
					v-for="(barSize, index) in ghostBarSizes"
					:key="index"
					class="chart-state__ghost-bar"
					:style="{ width: `${barSize}%` }"
				/>
			</div>
			<!-- Trend lines, filled beneath for the area shape. -->
			<svg
				v-else-if="isLineShape"
				class="chart-state__ghost-svg"
				viewBox="0 0 100 100"
				preserveAspectRatio="none"
				focusable="false"
			>
				<g
					v-for="(line, index) in ghostLineSeries"
					:key="index"
				>
					<polygon
						v-if="ghostShape === 'area'"
						class="chart-state__ghost-area"
						:points="areaPoints(line)"
					/>
					<polyline
						class="chart-state__ghost-line"
						:points="linePoints(line)"
					/>
				</g>
				<line
					class="chart-state__ghost-line"
					x1="0"
					y1="100"
					x2="100"
					y2="100"
				/>
			</svg>
			<!-- A ring, for pie and doughnut charts. Aspect ratio is kept
			here, unlike the other shapes, because a stretched circle stops
			looking like a pie. -->
			<svg
				v-else-if="ghostShape === 'pie'"
				class="chart-state__ghost-svg"
				viewBox="0 0 100 100"
				focusable="false"
			>
				<circle
					class="chart-state__ghost-ring"
					cx="50"
					cy="50"
					:r="ringRadius"
					:stroke-width="ringStrokeWidth"
				/>
			</svg>
		</div>
		<span
			v-if="isLoading"
			class="sr-only"
		>
			Loading chart data
		</span>
		<template v-else>
			<div class="chart-state__content">
				<v-icon
					v-if="presentation.showIcon"
					:size="32"
					:class="isError ? 'error--text' : 'primary--text'"
					data-testid="chart-state-icon"
				>
					{{ resolvedIcon }}
				</v-icon>
				<p
					class="chart-state__title"
					data-testid="chart-state-title"
				>
					{{ presentation.title }}
				</p>
				<p
					v-if="presentation.showDescription"
					class="chart-state__description"
					data-testid="chart-state-description"
				>
					{{ presentation.description }}
				</p>
				<v-btn
					v-if="presentation.showRetry && canRetry"
					class="chart-state__action"
					small
					outlined
					data-testid="chart-state-retry"
					@click="$emit('retry')"
				>
					<v-icon
						left
						small
					>
						mdi-refresh
					</v-icon>
					{{ retryLabel }}
				</v-btn>
			</div>
		</template>
	</div>
</template>

<script>
import {
	resolveStatePresentation,
	CHART_RETRY_LABEL,
	CHART_GHOST_BAR_HEIGHTS,
	CHART_GHOST_LINE_SERIES,
	CHART_GHOST_MAX_LINE_SERIES,
	CHART_GHOST_RING,
	CHART_GHOST_SHAPES
} from '../../utils/chart-data-state.js'

export default {
	name: 'chart-state',
	props: {
		/** Which state to render. Mutually exclusive by construction. */
		variant: {
			type: String,
			required: true,
			validator: value => ['loading', 'empty', 'error'].includes(value)
		},
		/**
		 * The host chart's height in px. Required so the card does not resize
		 * between states. There is deliberately no `width`: the state always
		 * fills the chart slot, so width is fixed at 100% in CSS.
		 */
		height: {
			type: Number,
			required: true
		},
		/**
		 * Which scaffold the ghost draws, so an empty chart resembles the chart
		 * that would have appeared with data. Chosen from what the chart
		 * renders rather than from which component draws it: a Chart.js bar
		 * chart with `indexAxis: 'y'` runs horizontally, and a line dataset
		 * with `fill` set draws as an area.
		 */
		ghostShape: {
			type: String,
			default: 'bar',
			validator: value => CHART_GHOST_SHAPES.includes(value)
		},
		/**
		 * How many series the chart plots, so a multi-series chart is not stood
		 * in for by a single line. Affects the line and area shapes only, and
		 * is clamped to the number of ghost series defined.
		 */
		ghostSeries: {
			type: Number,
			default: 1
		},
		/** Overrides the default title for this variant. */
		title: {
			type: String,
			default: null
		},
		/**
		 * Overrides the default description. Pass an empty string to suppress
		 * the description entirely.
		 */
		description: {
			type: String,
			default: null
		},
		/**
		 * Overrides the default glyph, as an `mdi-` name. Applies to whichever
		 * variant renders, the same as `title` and `description`, so a caller
		 * wanting to change only the empty glyph gates it on the variant.
		 *
		 * Worth setting wherever the copy has been overridden: the default
		 * glyph points at filters, and contradicting your own wording is worse
		 * than a generic icon.
		 */
		icon: {
			type: String,
			default: null
		}
	},
	computed: {
		isLoading () {
			return this.variant === 'loading'
		},
		isError () {
			return this.variant === 'error'
		},
		containerStyle () {
			// Bound as an object, never a string: Vue assigns object styles
			// through CSSOM, whereas a string would go via cssText, which the
			// portal's strict CSP blocks.
			return { height: `${this.height}px` }
		},
		resolvedIcon () {
			// Resolved here rather than in the shared helper: the two portals
			// draw from different icon libraries, so only the glyph's meaning
			// is shared, not its name. Paired with lucide SearchX and
			// AlertTriangle on the React side.
			//
			// The empty glyph is a magnifier, not a question mark: "your
			// filters matched nothing" is a blameless outcome, and a `?` reads
			// as "something is wrong", which crowds the error state's warning
			// triangle. The two must stay distinguishable by glyph alone,
			// since colour is never the only carrier.
			if (this.icon) return this.icon
			return this.isError ? 'mdi-alert-outline' : 'mdi-magnify-remove-outline'
		},
		presentation () {
			return resolveStatePresentation({
				variant: this.variant,
				height: this.height,
				title: this.title,
				description: this.description
			})
		},
		retryLabel () {
			return CHART_RETRY_LABEL
		},
		/**
		 * Whether a retry is on offer. Matches the React component, where
		 * omitting `onRetry` renders no button: an error state with a button
		 * that emits into nothing is a worse answer than one without.
		 *
		 * Read from `$listeners` because that is Vue 2's equivalent of an
		 * absent callback prop. It only means anything if the wrappers forward
		 * listeners transparently rather than binding `@retry` unconditionally,
		 * which is why they all use `v-on="$listeners"`.
		 *
		 * @returns {boolean} True if a caller is listening for retry
		 */
		canRetry () {
			return !!this.$listeners.retry
		},

		/**
		 * Whether the shape block renders at all. While loading it is the only
		 * content, so it survives every height; behind the empty and error copy
		 * it is decoration, and the cascade drops it at cramped heights.
		 *
		 * @returns {boolean} True if the shape should be drawn
		 */
		showShape () {
			return this.isLoading || this.presentation.showGhost
		},
		ghostBarSizes () {
			return CHART_GHOST_BAR_HEIGHTS
		},
		isLineShape () {
			return this.ghostShape === 'line' || this.ghostShape === 'area'
		},
		ghostLineSeries () {
			const count = Math.min(Math.max(this.ghostSeries, 1), CHART_GHOST_MAX_LINE_SERIES)
			return CHART_GHOST_LINE_SERIES.slice(0, count)
		},
		ringStrokeWidth () {
			return CHART_GHOST_RING.outerRadius - CHART_GHOST_RING.innerRadius
		},
		ringRadius () {
			return CHART_GHOST_RING.innerRadius + this.ringStrokeWidth / 2
		}
	},
	methods: {
		linePoints (line) {
			return line.map(point => `${point.x},${point.y}`).join(' ')
		},
		areaPoints (line) {
			return `0,100 ${this.linePoints(line)} 100,100`
		}
	}
}
</script>

<style scoped>
/*
 * Background is deliberately transparent. The chart card already supplies the
 * surface; nesting a grey panel inside it reads as a broken image placeholder
 * and would force every contrast check onto a second background.
 */
.chart-state {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 0 16px;
	overflow: hidden;
	text-align: center;
	background: transparent;
}

/*
 * Position and footprint shared by the ghost and the loading skeleton. Both
 * fill the plot area the real chart would have occupied, so a card in a
 * non-data state never looks like a shrunken one.
 *
 * The shapes are built from flex boxes rather than a fixed-viewBox SVG wherever
 * a box will do: a viewBox either letterboxes in a wide card or distorts its
 * own stroke widths when stretched to fill it.
 *
 * The bars carry a baseline only, no left axis: the vertical rule read as a
 * stray line rather than as part of a chart, since there are no tick labels for
 * it to belong to.
 */
.chart-state__shape {
	position: absolute;
	top: 20px;
	right: 24px;
	bottom: 20px;
	left: 24px;
	display: flex;
	pointer-events: none;
}

/* The ghost behind the empty and error copy: barely there, and purely */
/* decorative. Low opacity over the foreground role rather than a fixed light */
/* grey, so one declaration holds in both themes. */
.chart-state__ghost {
	color: var(--global-foreground);
	opacity: 0.07;
}

/* The loading skeleton, drawn from the same geometry as the ghost so a chart */
/* cannot pulse as one shape and then settle as another. Heavier than the */
/* ghost, because here the shape is the content: there is no copy in front of */
/* it competing for attention. */
/* Translucent black and white rather than a neutral surface token, matching */
/* the React side's foreground role at 15%. A surface token is the wrong */
/* family for a foreground shape: the ones available here sit within a few */
/* percent of the card in dark, which is how the React implementation ended up */
/* painting its skeleton the exact colour of the card behind it. The base */
/* translucency lives in the colour rather than in `opacity`, because the */
/* pulse animates `opacity` and would override it. */
.chart-state__skeleton {
	/* Fallback for anything without color-mix. Light-theme value, so the */
	/* worst case is a slightly-off skeleton rather than an invisible one, */
	/* which the theme rules below still correct. */
	color: rgba(0, 0, 0, 0.15);
	color: color-mix(in srgb, var(--global-foreground) 15%, transparent);
	animation: chart-state-pulse 1.5s ease-in-out infinite;
}

/* Belt and braces for the fallback path: the ghost derives its colour from */
/* --global-foreground, which flips with the theme, and so does the color-mix */
/* above. These only matter where color-mix is unsupported. */
.theme--dark .chart-state__skeleton,
.dark .chart-state__skeleton {
	color: rgba(255, 255, 255, 0.15);
	color: color-mix(in srgb, var(--global-foreground) 15%, transparent);
}

.chart-state__ghost-plot {
	display: flex;
	flex: 1;
	align-items: flex-end;
	gap: 2.5%;
	padding: 0 2.5%;
	border-bottom: 2px solid currentColor;
}

.chart-state__ghost-bar {
	flex: 1;
	background: currentColor;
}

/* Same bars, rotated: sizes become widths and they grow from a left axis. */
.chart-state__ghost-plot--horizontal {
	flex-direction: column;
	align-items: flex-start;
	justify-content: space-between;
	padding: 2.5% 0;
	border-bottom: none;
	border-left: 2px solid currentColor;
}

.chart-state__ghost-svg {
	flex: 1;
	width: 100%;
	height: 100%;
}

.chart-state__ghost-line {
	fill: none;
	stroke: currentColor;
	stroke-width: 2;
	stroke-linejoin: round;
	stroke-linecap: round;
	/* Without this, stretching the viewBox to the card width thickens the */
	/* stroke horizontally. It is the reason the bars are boxes rather than */
	/* SVG, and the reason the line shapes can safely be SVG. */
	vector-effect: non-scaling-stroke;
}

.chart-state__ghost-area {
	/* Stacked fills would compound into a solid block, so each stays faint */
	/* enough that the individual lines still read. */
	fill: currentColor;
	opacity: 0.3;
}

.chart-state__ghost-ring {
	fill: none;
	stroke: currentColor;
}

/* Stacks above the ghost. */
.chart-state__content {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

/*
 * Declared locally even though an unscoped .sr-only is present globally (it
 * ships in the vendor bundle and in app.css). This component's hidden loading
 * label must not depend on ambient CSS it does not own: if that global ever
 * moves, the label would silently become visible text on every chart load.
 */
.chart-state .sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

.chart-state__title {
	margin: 8px 0 0;
	font-size: 14px;
	font-weight: 500;
	color: var(--global-foreground);
}

.chart-state__description {
	margin: 4px 0 0;
	max-width: 32ch;
	font-size: 12px;
	font-weight: 400;
	color: var(--global-muted-foreground);
}

.chart-state__action {
	margin-top: 12px;
}

@keyframes chart-state-pulse {
	0%, 100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}

@media (prefers-reduced-motion: reduce) {
	.chart-state__skeleton {
		animation: none;
	}
}
</style>
